import { PyodideFileJournal } from '../journal/python-journal.helper';
import { PYTHON_DIRECTORIES, PYFS_CACHE_KEYS, CleanupResult } from '../interfaces/python.interface';
import { STEPS_NAMESPACE } from '../../../constants/general.constants';
import { PythonHelper } from '../helpers/python.helper';
import { NotebookFile } from '../../../interfaces/file.interface';
import { LocalforageService } from '../../localforage/localforage.service';

/**
 * PythonCleanupHelper
 *
 * Centralized cleanup operations for Python filesystem and modules.
 * Extracted from PythonService to reduce monolithic service size.
 *
 * Responsibilities:
 * - Clean up filesystem using journal metadata (DELETE BY DEFAULT)
 * - Clean up Python modules from sys.modules
 * - Scan and manage mounted files
 * - Unmount missing files
 * - Synchronize Journal + LocalforageService cache on deletions
 */
export class PythonCleanupHelper {
  constructor(
    private readonly python: any,
    private readonly fileJournal: PyodideFileJournal,
    private readonly localforageService?: LocalforageService
  ) { }

  /**
   * Simple cleanup using journal-based approach
   * Only removes what we tracked and steps.* modules
   */
  async cleanupProject(loadedPackages?: Set<string>): Promise<CleanupResult> {
    const startTime = performance.now();

    try {
      // Get journal summary before cleanup
      const _journalSummary = this.fileJournal.getJournalSummary();

      // STEP 1: Clean up filesystem USING journal (before clearing it!)
      // This is where "Delete by Default" happens
      const fsCleanup = await this.cleanupFileSystem();

      // STEP 2: Clean up Python modules
      await this.cleanupPythonModules();

      // STEP 3: Clear the journal AFTER using it
      this.fileJournal.clear();

      // STEP 4: Clear loaded packages tracking (packages may be different in next workflow)
      if (loadedPackages) {
        loadedPackages.clear();
      }

      // Return detailed result
      const result: CleanupResult = {
        filesRemoved: fsCleanup.removed,
        directoriesRemoved: 0,
        modulesRemoved: 0,
        packagesRemoved: 0,
        duration: performance.now() - startTime,
        success: true,
        errors: fsCleanup.errors
      };

      return result;

    } catch (error: any) {

      return {
        filesRemoved: 0,
        directoriesRemoved: 0,
        modulesRemoved: 0,
        packagesRemoved: 0,
        duration: performance.now() - startTime,
        success: false,
        errors: [error.message || 'Unknown cleanup error']
      };
    }
  }

  /**
   * Clean up filesystem using journal metadata
   * Removes files where persistent=false (DELETE BY DEFAULT)
   */
  async cleanupFileSystem(): Promise<{ kept: number; removed: number; errors: string[] }> {
    const result = { kept: 0, removed: 0, errors: [] as string[] };

    // PRIORITY 3 FIX: Protected directories that should NEVER be deleted
    const protectedDirs = new Set([
      `${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.FILES_SUBDIR}`,
      `${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.DEFAULT_SUBDIR}`,
      `${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.PROJECT_SUBDIR}`
    ]);

    try {
      // Get all files from journal (efficient - no filesystem scan needed!)
      const fileMetadata = this.fileJournal?.getFileMetadata();

      if (!fileMetadata || fileMetadata.size === 0) {
        return result;
      }

      // Iterate through journaled files (O(n) instead of O(filesystem))
      for (const [filePath, metadata] of fileMetadata.entries()) {
        // Skip protected structural directories (defense in depth)
        if (protectedDirs.has(filePath)) {
          result.kept++;
          continue;
        }

        // Check persistent flag (DELETE BY DEFAULT)
        if (metadata.persistent) {
          result.kept++;
        } else {
          try {
            // Check if file exists before trying to delete
            if (this.python.FS.analyzePath(filePath).exists) {
              this.python.FS.unlink(filePath);
              result.removed++;
            }
          } catch (error: any) {
            result.errors.push(`Failed to remove ${filePath}: ${error.message}`);
          }
        }
      }

    } catch (error: any) {
      result.errors.push(`Filesystem cleanup failed: ${error.message}`);
    }

    return result;
  }

  /**
   * Clean only steps.* modules that we registered for user code
   * These are the ONLY modules we have control over and should clean
   */
  async cleanupPythonModules(): Promise<void> {
    try {
      const cleanupResult = this.python.runPython(`
        import sys

        # Find all ${STEPS_NAMESPACE}.* modules
        modules_to_remove = [m for m in list(sys.modules.keys()) if m.startswith('${STEPS_NAMESPACE}.')]

        # Log modules that will be removed
        if modules_to_remove:
          print(f"🐍 Found {len(modules_to_remove)} ${STEPS_NAMESPACE} modules to remove: {', '.join(modules_to_remove)}")
        else:
          print("🐍 No ${STEPS_NAMESPACE} modules found to remove")

        # Remove each module
        removed_modules = []
        for module in modules_to_remove:
          try:
            del sys.modules[module]
            removed_modules.append(module)
            print(f"  ✅ Removed module: {module}")
          except Exception as e:
            print(f"  ⚠️ Could not remove module {module}: {e}")

        print(f"🐍 Successfully removed {len(removed_modules)} ${STEPS_NAMESPACE} modules from sys.modules")
        {'removed_count': len(removed_modules), 'removed_modules': removed_modules}
      `) || { success: false, value: null };
      const result = cleanupResult.success ? cleanupResult.value : null;

      // Extract the result using PythonHelper
      const _moduleResult = result ? PythonHelper.pythonToJs(result, this.python) : null;
    } catch (error) {
      console.warn('Python cleanup: failed to remove step modules', error);
    }
  }

  /**
   * Recursively get all files in a directory
   * @param directory - Directory path to scan
   * @returns Array of all file paths
   */
  getAllFilesRecursive(directory: string): string[] {
    const files: string[] = [];

    // Check if directory exists before scanning
    try {
      const stats = this.python.FS.stat(directory);
      if (!this.python.FS.isDir(stats.mode)) {
        return files;
      }
    } catch (error) {
      console.debug('Python cleanup: directory stat failed', { directory, error });
      return files;
    }

    try {
      const scan = (dir: string) => {
        try {
          const items = this.python.FS.readdir(dir);
          for (const item of items) {
            if (item === '.' || item === '..') continue;

            const path = `${dir}/${item}`;
            try {
              const stat = this.python.FS.stat(path);
              if (this.python.FS.isFile(stat.mode)) {
                files.push(path);
              } else if (this.python.FS.isDir(stat.mode)) {
                scan(path); // Recursive scan
              }
            } catch (error) {
              console.debug('Python cleanup: skip file stat failure', { path, error });
            }
          }
        } catch (error) {
          console.debug('Python cleanup: skip unreadable directory', { dir, error });
        }
      };

      scan(directory);
    } catch (error) {
      console.warn('Python cleanup: error scanning directory', { directory, error });
    }

    return files;
  }

  /**
   * Gets paths of all mounted files in the Pyodide filesystem
   * @returns Array of file paths
   */
  getMountedFilePaths(): string[] {
    const filePaths: string[] = [];
    const iterateFileSystem = (startPath = '/') => {
      const explore = (path: string) => {
        try {
          const contents = this.python.FS.readdir(path);
          contents.forEach((item: any) => {
            if (item === '.' || item === '..') {
              // Skip the current and parent directory entries
              return;
            }

            const fullPath = `${path}/${item}`;
            try {
              const stats = this.python.FS.stat(fullPath);

              if (this.python.FS.isDir(stats.mode)) {
                // If it's a directory, recurse into it
                explore(fullPath);
              } else if (this.python.FS.isFile(stats.mode)) {
                // If it's a file, add to list
                filePaths.push(fullPath);
              }
            } catch (error) {
              console.debug('Python cleanup: error accessing path', { fullPath, error });
            }
          });
        } catch (error) {
          console.debug('Python cleanup: error reading directory', { path, error });
        }
      };

      try {
        explore(startPath);
      } catch (error) {
        console.warn('Python cleanup: filesystem iteration failed', { startPath, error });
      }
    };

    iterateFileSystem();
    return filePaths;
  }

  /**
   * Get mounted files for backend sync
   * Filters based on timestamp and persistent flag
   *
   * @param currentRunStartTimestamp - Timestamp of current run start
   * @param workingDir - Working directory to scan
   * @returns Map of files to sync (fullPath -> timestamp string)
   */
  getMountedFiles(
    currentRunStartTimestamp?: number,
    workingDir?: string
  ): Map<string, string> {
    const files = new Map<string, string>();
    let _checkedCount = 0;
    let _preservedCount = 0;
    let _skippedOrphanedCount = 0;
    const runStart = currentRunStartTimestamp || 0;

    // ✅ RESTORED FROM fe813d8: FS scan + journal check (hybrid approach)
    // This finds ALL files (including PyArrow) and uses journal for filtering
    const scanDirectory = (path: string) => {
      try {
        const contents = this.python.FS.readdir(path);

        for (const item of contents) {
          if (item === '.' || item === '..') continue;

          const fullPath = `${path}/${item}`;

          try {
            const stats = this.python.FS.stat(fullPath);

            if (this.python.FS.isFile(stats.mode)) {
              _checkedCount++;

              // Get metadata from journal
              const metadata = this.fileJournal.getFileMetadata().get(fullPath);

              // ALSO check journal.shouldKeepFile() for backwards compatibility (fe813d8)
              const shouldKeep = this.fileJournal.shouldKeepFile(fullPath) ||
                this.fileJournal.shouldKeepFile(item) ||
                this.fileJournal.shouldKeepFile(`./${item}`);

              // Get timestamp: prefer journal metadata, fallback to mtime.
              // mtime is load-bearing for unjournaled Python writes. Idioms
              // that preserve source mtime (shutil.copy2, zipfile.extract,
              // tarfile.extractall) can stamp a time before runStart and are
              // then skipped — same hole as before this PR, now the only arm.
              const fileTimestamp = metadata?.timestamp || stats.mtime.getTime();
              const isCreatedDuringRun = fileTimestamp >= runStart;

              // Get persistent flag: default to true if no metadata (user-created files)
              const isPersistent = metadata?.persistent ?? true;

              // EHQ-104: Only sync files touched in the current run.
              // Re-syncing every preserved backend file each run caused hash/compare
              // (and sometimes download) work to grow with total mounted files.
              //
              // If runStart is unavailable (defensive fallback), keep tracked
              // persistent files so we do not silently drop generated output.
              const canUseRunBoundary = runStart > 0;
              const shouldSyncFromRun = isPersistent && isCreatedDuringRun;
              const shouldSyncWithoutRunBoundary = isPersistent && shouldKeep;

              if ((canUseRunBoundary && shouldSyncFromRun) || (!canUseRunBoundary && shouldSyncWithoutRunBoundary)) {
                // CRITICAL FIX: Use fullPath as key to prevent timestamp collision
                files.set(fullPath, fileTimestamp.toString());
                _preservedCount++;
              } else {
                // File NOT from current run (and not covered by fallback) → skip
                _skippedOrphanedCount++;
              }
            } else if (this.python.FS.isDir(stats.mode) &&
              !fullPath.includes('node_modules') &&
              !fullPath.includes('__pycache__')) {
              // Recurse into subdirectories
              scanDirectory(fullPath);
            }
          } catch (_statError) {
            // Could not stat path
          }
        }
      } catch (_readdirError) {
        // Could not read directory
      }
    };

    // Scan from working directory or default
    const startPath = workingDir || '/home/pyodide';
    scanDirectory(startPath);

    return files;
  }

  /**
   * Unmounts files that exist in current notebookFiles but not in the provided notebookFiles argument
   * @param currentNotebookFiles - Current array of notebook files
   * @param newNotebookFiles - New array of notebook files to compare against
   * @param workingDir - Working directory path
   * @param isLoaded - Whether Python runtime is loaded
   */
  async unmountMissingFiles(
    currentNotebookFiles: NotebookFile[],
    newNotebookFiles: NotebookFile[],
    workingDir: string,
    isLoaded: boolean
  ): Promise<void> {
    if (!this.python || !isLoaded) {
      return; // Skip if Python runtime is not loaded
    }

    try {
      // Create a set of new notebook file names for quick lookup
      const newNotebookFileNames = new Set(newNotebookFiles.map(file => file.name));

      // Identify files that exist in current notebookFiles but not in new notebookFiles
      const filesToRemove = currentNotebookFiles
        .filter(file => !newNotebookFileNames.has(file.name));

      if (filesToRemove.length === 0) {
        return;
      }

      // Keep track of how many files were successfully removed
      let _removedCount = 0;
      const cacheKeysToRemove: string[] = [];  // Batch cache removal

      for (const file of filesToRemove) {
        // Use metadata to determine CORRECT subdirectory (same logic as mounting)
        const subdirectory = this.getFileSubdirectoryForDeletion(file);
        const primaryPath = `${workingDir}/${subdirectory ? subdirectory + '/' : ''}${file.name}`;

        // Delete from primary path only (no duplicate cleanup)
        try {
          // Layer 1: Unmount from Pyodide FS
          this.python.FS.unlink(primaryPath);

          // Layer 2: Remove from Journal
          this.fileJournal.removeFile(primaryPath);

          // Layer 3: Queue for LocalforageService removal
          cacheKeysToRemove.push(`${PYFS_CACHE_KEYS.META_PREFIX}${primaryPath}`);

          _removedCount++;
        } catch (error) {
          console.warn('Python cleanup: error unmounting file', { primaryPath, error });
        }

        // Deletion targets only the metadata-resolved primary path (computed above).
        // An earlier all-matching-paths sweep was removed because it deleted files
        // from ROOT (os.listdir(".")) instead of the intended subdirectory.
      }

      // Batch remove from LocalforageService
      if (this.localforageService && cacheKeysToRemove.length > 0) {
        try {
          await Promise.all(
            cacheKeysToRemove.map(key =>
              this.localforageService!.removeItem(key)
                .catch((error_) => {
                  console.warn('Python cleanup: failed to remove cache key', { key, error: error_ });
                })
            )
          );
        } catch (error) {
          console.warn('Python cleanup: batch cache removal failed', error);
        }
      }

    } catch (error) {
      console.warn('Python cleanup: non-fatal cleanup error', error);
    }
  }

  /**
   * Determine file subdirectory based on NotebookFile metadata
   * Uses same routing logic as PythonFileSystemHelper.getFileSubdirectory()
   * This ensures deletion uses the SAME path as mounting
   *
   * @param file - NotebookFile with metadata
   * @returns Subdirectory path ('files', 'default', 'project', or '')
   */
  private getFileSubdirectoryForDeletion(file: NotebookFile): string {
    return PythonHelper.resolveFileSubdirectory(file);
  }

  /**
   * Cleanup stale metadata from Journal and LocalforageService
   * Compares backend file list (source of truth) with cached metadata
   * Removes entries for files that no longer exist in backend
   */
  async cleanupStaleMetadata(expectedFiles: NotebookFile[], workingDir: string): Promise<void> {
    const expectedNames = new Set(expectedFiles.map(f => f.name));
    let _journalCleaned = 0;
    let _cacheCleaned = 0;
    let _fsCleaned = 0;

    try {
      // 1. Clean Journal
      const journalMeta = this.fileJournal.getFileMetadata();
      for (const [path, _meta] of journalMeta.entries()) {
        const fileName = path.split('/').pop() || '';

        if (!expectedNames.has(fileName)) {
          // File in Journal but not in backend → delete
          this.fileJournal.removeFile(path);
          _journalCleaned++;
        }
      }

      // 2. Clean LocalforageService
      // SCRUM-166: Parallelize stale key removal instead of sequential for...of
      if (this.localforageService) {
        const keys = await this.localforageService.keys();
        const pyfsKeys = keys.filter(
          (k: string) => k.startsWith(PYFS_CACHE_KEYS.META_PREFIX) || k.startsWith(PYFS_CACHE_KEYS.SYNCED_PREFIX),
        );

        const staleKeys = pyfsKeys.filter((key: string) => {
          const path = key.replace(PYFS_CACHE_KEYS.META_PREFIX, '').replace(PYFS_CACHE_KEYS.SYNCED_PREFIX, '');
          const fileName = path.split('/').pop() || '';
          return !expectedNames.has(fileName);
        });

        if (staleKeys.length > 0) {
          await Promise.all(
            staleKeys.map(key => this.localforageService!.removeItem(key).catch(() => {}))
          );
          _cacheCleaned = staleKeys.length;
        }
      }

      // 3. Clean Pyodide FS (user directories only)
      const userDirs = [
        `${workingDir}/files/`,
        `${workingDir}/project/`,
        `${workingDir}/default/`
      ];

      const mountedPaths = this.getMountedFilePaths();

      for (const path of mountedPaths) {
        // Skip if not in user directories
        if (!userDirs.some(dir => path.startsWith(dir))) {
          continue;
        }

        const fileName = path.split('/').pop() || '';

        if (!expectedNames.has(fileName)) {
          // File in FS but not in backend → delete
          try {
            this.python.FS.unlink(path);
            _fsCleaned++;
          } catch (error_) {
            console.debug('Python cleanup: failed to remove stale file', { path, error: error_ });
          }
        }
      }

    } catch (error) {
      console.warn('Python cleanup: stale metadata cleanup failed', error);
    }
  }
}
